import videojs from 'video.js'
import lodash from 'lodash'
import moment from 'moment'

const defaults = {
  chat: null,
  summary: null,
  captions: null
}

const dom = videojs.dom || videojs

const Button = videojs.getComponent('Button')
const Plugin = videojs.getPlugin('plugin')
const Component = videojs.getComponent('Component')

class ProeducaChat extends Plugin {
  /**
   * Constructor
   *
   * @param {Player|Object} player The player
   * @param {Object=} options Button options
   */
  constructor (player, options) {
    super(player, options)
    const settings = videojs.mergeOptions(defaults, options)
    // El panel agrupa varios contenidos de la grabación en pestañas, así que el botón se añade
    // si hay cualquiera de ellos. Cada pestaña decide por su cuenta si aparece.
    const hasChat = Boolean(settings.chat)
    const hasSummary = Boolean(settings.summary && String(settings.summary).trim())
    // La transcripción sale de las cues del propio track de subtítulos, así que aquí basta con
    // saber si la grabación tiene VTT; el contenido lo carga el panel cuando el track esté listo.
    const hasTranscript = Boolean(settings.captions)

    this.messages = []
    if (!hasChat && !hasSummary && !hasTranscript) {
      return
    }

    if (hasChat) {
      let messagesData = JSON.parse(settings.chat)
      let t = this
      lodash.each(messagesData, function (e) {
        e.eventTime = moment(e.eventTime).unix()
        e.relativeEventTime = moment.duration(e.relativeEventTime, 'hh:mmm:ss').asSeconds()
        e.filteredBody = e.body
        e.srBody = e.body
        t.messages.push(e)
      })
      this.messages = lodash.sortBy(this.messages, ['eventTime'])
    }

    player.addClass('vjs-proeduca-chat')
    player.controlBar.proeducaChat = player.controlBar.addChild('proeducaChatButton', {
      'hasChat': hasChat,
      'hasTranscript': hasTranscript,
      'messages': this.messages,
      'summary': hasSummary ? settings.summary : null
    })
  }
}

/**
 * Componente que representa el panel de contenidos de la grabación, organizado en pestañas.
 * De momento hay dos: el chat y el resumen. Cada una solo se registra si hay contenido para ella,
 * así que con un único contenido se ve una sola pestaña.
 *
 * @extends Component
 * @class SeekButton
 */
class ProeducaChatComponent extends Component {
  /**
   * Constructor
   *
   * @param {Player|Object} player The player
   * @param {Object=} options Button options
   */
  constructor (player, options) {
    super(player, options)

    // El panel por defecto estará oculto
    this.isVisible = false
    this.on(player, 'chat', this.manageChatEvent_)
    // Nuevo abre su panel de capítulos añadiendo una clase desde el onclick de su propio botón,
    // sin avisar por ningún evento del player, así que hay que escuchar el clic. Va por delegación
    // porque ese botón no existe hasta que Nuevo termina de leer el VTT de capítulos.
    this.on(player.el(), 'click', this.closeOnChaptersOpen_)
    // Se generan las pestañas disponibles y el árbol de componentes de cada una
    this.tabs_ = []
    this.buildTabs_()
    this.buildCloseButton_()
  }

  createEl () {
    const el = dom.createEl('div', {
      className: 'vjs-proeduca-chat-box vjs-hidden'
    })
    const header = dom.createEl('header', {
      className: ''
    })
    // La tira de pestañas vive en la cabecera, donde antes estaba el título fijo del chat
    this.tabsEl_ = dom.createEl('nav', {
      className: 'vjs-proeduca-tabs'
    }, {
      role: 'tablist'
    })
    dom.appendContent(header, this.tabsEl_)
    dom.appendContent(el, header)
    // La cabecera se construye aquí, pero el botón de cerrar necesita el árbol de componentes
    // (addChild) para engancharse al ciclo de vida, y eso no puede hacerse desde createEl.
    this.headerEl_ = header
    return el
  }

  /**
   * Añade a la cabecera el botón de cerrar el panel, igual que el que trae el panel de capítulos
   * de Nuevo.
   */
  buildCloseButton_ () {
    const button = new ProeducaCloseButton(this.player())
    // Mismo apaño que en addTab_: addChild cuelga el elemento de la caja y hay que moverlo.
    this.addChild(button)
    dom.appendContent(this.headerEl_, button.el())
  }

  buildTabs_ () {
    if (this.options().hasChat) {
      this.addTab_('chat', this.localize('Chat'), this.buildChatPanel_())
    }
    if (this.options().hasTranscript) {
      this.addTab_('transcript', this.localize('Transcript'), this.buildTranscriptPanel_())
    }
    if (this.options().summary) {
      this.addTab_('summary', this.localize('Summary'), this.buildSummaryPanel_())
    }
    if (this.tabs_.length > 0) {
      this.selectTab_(this.tabs_[0].name)
    }
  }

  /**
   * Registra una pestaña: su botón en la cabecera y el panel con su contenido.
   *
   * @param {string} name Identificador interno de la pestaña
   * @param {string} label Etiqueta ya traducida que se muestra al usuario
   * @param {Component} panel Componente con el contenido de la pestaña
   */
  addTab_ (name, label, panel) {
    const button = new ProeducaTabButton(this.player(), {
      label: label,
      name: name,
      onSelect: (selected) => this.selectTab_(selected)
    })
    // addChild registra el hijo para que se destruya con el panel, pero al hacerlo cuelga su
    // elemento de la caja. El botón va en la cabecera, así que se mueve al nav después.
    this.addChild(button)
    dom.appendContent(this.tabsEl_, button.el())
    this.addChild(panel)
    this.tabs_.push({name: name, button: button, panel: panel})
  }

  /**
   * Muestra el panel de la pestaña indicada y oculta el resto.
   *
   * @param {string} name Identificador de la pestaña a activar
   */
  selectTab_ (name) {
    lodash.each(this.tabs_, function (tab) {
      const isActive = tab.name === name
      if (isActive) {
        tab.button.addClass('vjs-proeduca-tab-active')
        tab.panel.removeClass('vjs-hidden')
      } else {
        tab.button.removeClass('vjs-proeduca-tab-active')
        tab.panel.addClass('vjs-hidden')
      }
      tab.button.el().setAttribute('aria-selected', isActive ? 'true' : 'false')
    })
  }

  /**
   * Cierra este panel cuando se abre el de capítulos de Nuevo. Los dos se colocan sobre el borde
   * derecho del reproductor y el de capítulos va por encima (z-index 6), así que taparía la mitad
   * de este. Se cierra por el mismo evento que usa el botón, para no duplicar la lógica.
   *
   * @param {Event} event Clic en cualquier punto del reproductor
   */
  closeOnChaptersOpen_ (event) {
    if (!this.isVisible || !event.target.closest) {
      return
    }
    if (event.target.closest('.vjs-chapters-list')) {
      this.player().trigger('chat')
    }
  }

  manageChatEvent_ () {
    if (this.isVisible === false) {
      // El recíproco de closeOnChaptersOpen_: al abrir este panel se cierra el de capítulos.
      // Nuevo no expone API para cerrarlo, solo la clase con la que lo muestra.
      const chapters = this.player().el().querySelector('.vjs-chplaylist')
      if (chapters) {
        chapters.classList.remove('vjs-chplaylist-show')
      }
      this.player().addClass('vjs-proeduca-chat-box-open')
      this.removeClass('vjs-hidden')
      this.isVisible = true
    } else {
      this.player().removeClass('vjs-proeduca-chat-box-open')
      this.addClass('vjs-hidden')
      this.isVisible = false
    }
  }

  /**
   * Panel del chat. La lista de mensajes se construye igual que antes de haber pestañas; lo único
   * que cambia es que ahora cuelga de un panel en lugar de la caja. El CSS sigue valiendo porque
   * apunta al ul por descendencia.
   *
   * @return {Component} Panel con la lista de mensajes
   */
  buildChatPanel_ () {
    let panel = new Component(this.player(), {
      'el': dom.createEl('div', {className: 'vjs-proeduca-tab-panel'})
    })
    const el = dom.createEl('ul', {className: 'chat-playback-list'})
    let messagesListComponent = new Component(this.player(), {'el': el})
    let messages = this.options().messages
    lodash.each(messages, function (m, i) {
      let endSeconds = null
      if (messages[i + 1]) {
        endSeconds = messages[i + 1].relativeEventTime
      }
      messagesListComponent.addChild('ProeducaChatEntryComponent', {message: m, endSeconds: endSeconds})
    })
    panel.addChild(messagesListComponent)
    return panel
  }

  /**
   * Panel de la transcripción. Se monta vacío porque las cues del VTT no están disponibles todavía:
   * el track de subtítulos se añade al reproductor en el evento 'loadeddata' (ver VideoPlayer.vue) y
   * el fichero se descarga después. La lista se rellena cuando llegan, en loadTranscriptCues_.
   *
   * @return {Component} Panel con la lista de intervenciones
   */
  buildTranscriptPanel_ () {
    let panel = new Component(this.player(), {
      'el': dom.createEl('div', {className: 'vjs-proeduca-tab-panel'})
    })
    // Mismas clases que la lista del chat: comparten diseño y comportamiento
    const el = dom.createEl('ul', {className: 'chat-playback-list'})
    this.transcriptList_ = new Component(this.player(), {'el': el})
    panel.addChild(this.transcriptList_)
    this.loadTranscriptCues_()
    return panel
  }

  /**
   * Espera al track de subtítulos y vuelca sus cues en la lista de la transcripción.
   *
   * Hay dos esperas encadenadas: el track todavía no existe cuando se construye el panel, y cuando
   * existe aún no tiene cues. Ademas hay que forzarle el modo 'hidden': video.js NO descarga los
   * VTT de kind captions/subtitles hasta que dejan de estar en 'disabled' (los de otros kind, como
   * chapters, sí los baja de entrada), y sin descarga no hay cues. 'hidden' las carga sin activar
   * los subtítulos sobre el vídeo, que solo se pintan en modo 'showing'.
   */
  loadTranscriptCues_ () {
    const tracks = this.player().textTracks()
    const findTrack = () => {
      for (let i = 0; i < tracks.length; i++) {
        if (tracks[i].kind === 'captions' || tracks[i].kind === 'subtitles') {
          return tracks[i]
        }
      }
      return null
    }
    const useTrack = (track) => {
      const fill = () => this.fillTranscript_(track)
      if (track.cues && track.cues.length > 0) {
        fill()
      } else {
        track.addEventListener('loadeddata', fill)
      }
      if (track.mode === 'disabled') {
        track.mode = 'hidden'
      }
    }
    const track = findTrack()
    if (track) {
      useTrack(track)
      return
    }
    this.on(tracks, 'addtrack', function (event) {
      if (event.track && (event.track.kind === 'captions' || event.track.kind === 'subtitles')) {
        useTrack(event.track)
      }
    })
  }

  /**
   * Construye una entrada por cada cue del VTT, reutilizando el componente del chat: el texto va
   * donde el cuerpo del mensaje y el inicio de la cue donde su minutaje, que es el que lleva el
   * salto al vídeo. No se pasa userName porque el VTT no distingue quien habla.
   *
   * @param {TextTrack} track Track de subtítulos ya cargado
   */
  fillTranscript_ (track) {
    const cues = track.cues
    // El flag evita duplicar la lista si el track llega por las dos vias (ya presente y 'addtrack')
    // o si la grabación tuviera más de una pista de subtítulos.
    if (!this.transcriptList_ || !cues || cues.length === 0 || this.transcriptFilled_) {
      return
    }
    this.transcriptFilled_ = true
    for (let i = 0; i < cues.length; i++) {
      this.transcriptList_.addChild('ProeducaChatEntryComponent', {
        message: {
          relativeEventTime: cues[i].startTime,
          body: cues[i].text
        },
        endSeconds: cues[i + 1] ? cues[i + 1].startTime : cues[i].endTime,
        plainBody: true
      })
    }
  }

  /**
   * Panel del resumen. El resumen es texto plano (así lo trata la descarga en .txt del listado de
   * sesiones), de ahí el textContent en lugar de innerHTML: los saltos de línea los respeta el CSS
   * con white-space y no se inyecta HTML que venga del contenido.
   *
   * @return {Component} Panel con el texto del resumen
   */
  buildSummaryPanel_ () {
    let panel = new Component(this.player(), {
      'el': dom.createEl('div', {className: 'vjs-proeduca-tab-panel'})
    })
    const summary = dom.createEl('div', {className: 'vjs-proeduca-summary'})
    summary.textContent = this.options().summary
    dom.appendContent(panel.el(), summary)
    return panel
  }
}
videojs.registerComponent('ProeducaChatComponent', ProeducaChatComponent)

/**
 * Botón de una pestaña del panel de contenidos
 *
 * @extends Button
 * @class SeekButton
 */
class ProeducaTabButton extends Button {
  createEl () {
    const el = dom.createEl('button', {
      className: 'vjs-proeduca-tab'
    }, {
      type: 'button',
      role: 'tab'
    })
    el.textContent = this.options().label
    return el
  }

  /**
   * Activa la pestaña asociada al botón
   */
  handleClick () {
    this.options().onSelect(this.options().name)
  }
}
videojs.registerComponent('ProeducaTabButton', ProeducaTabButton)

/**
 * Botón de cerrar del panel de contenidos
 *
 * @extends Button
 */
class ProeducaCloseButton extends Button {
  createEl () {
    const el = dom.createEl('button', {
      className: 'vjs-proeduca-close'
    }, {
      type: 'button',
      'aria-label': this.localize('Close')
    })
    // Se reproduce el icono que genera Nuevo para su propia X: un <use> contra el sprite SVG que
    // el skin trae embebido en el CSS y que Nuevo inyecta en el reproductor al arrancar. Aquí se
    // escribe a mano porque su rutina de reemplazo de iconos ya ha pasado cuando se crea el panel.
    el.innerHTML = '<span class="vjs-svg-icon" aria-hidden="true">' +
      '<svg viewBox="0 0 32 32"><use href="#vjs-icon-close" xlink:href="#vjs-icon-close"></use></svg>' +
      '</span>'
    return el
  }

  /**
   * Cierra el panel por la misma vía que el botón de la barra de control
   */
  handleClick () {
    this.player().trigger('chat')
  }
}
videojs.registerComponent('ProeducaCloseButton', ProeducaCloseButton)

/**
 * Componente que representa una entrada de conversación del chat
 * Escuchará eventos de actualización de tiempo de reproducción para marcar el estado (futuro, activo, pasado)
 *
 * @extends Component
 * @class SeekButton
 */
class ProeducaChatEntryComponent extends Component {
  /**
   * Constructor
   *
   * @param {Player|Object} player The player
   * @param {Object=} options Button options
   */
  constructor (player, options) {
    super(player, options)

    let chatMessageComponent = new Component(this.player(), {'el': dom.createEl('div', {className: 'playback-chat-message chat-message'})})
    let chatMessageContentComponent = new Component(this.player(), {'el': dom.createEl('div', {className: 'activity-message chat-message__content'})})

    // La transcripción no trae nombre (el VTT no distingue quién habla), y entonces no se pinta el
    // hueco: el minutaje se queda pegado a la derecha por su `margin-left: auto`.
    if (this.options().message.userName) {
      const chatMessageName = dom.createEl('h4', {className: 'playback-chat-name chat-message__name'})
      chatMessageName.innerHTML = this.options().message.userName
      dom.appendContent(chatMessageContentComponent.el(), chatMessageName)
    }

    let chatMessageTimeComponent = new Component(this.player(), {'el': dom.createEl('div', {className: 'playback-chat-time chat-message__time'})})
    let chatTimeButton = new ProeducaChatTimeButton(this.player(), {text: moment(this.options().message.relativeEventTime * 1000).format('mm:ss'), seconds: this.options().message.relativeEventTime})
    chatMessageTimeComponent.addChild(chatTimeButton)
    chatMessageContentComponent.addChild(chatMessageTimeComponent)

    const chatMessageBody = dom.createEl('div', {className: 'playback-chat-body chat-message__body'})
    // El chat llega como HTML desde Collaborate, pero el texto de una cue de VTT se pinta tal cual:
    // ni se inyecta el marcado que pueda traer el fichero ni hace falta interpretarlo.
    if (this.options().plainBody) {
      chatMessageBody.textContent = this.options().message.body
    } else {
      chatMessageBody.innerHTML = this.options().message.body
    }
    dom.appendContent(chatMessageContentComponent.el(), chatMessageBody)

    chatMessageComponent.addChild(chatMessageContentComponent)
    this.addChild(chatMessageComponent)

    // Manejo de cambio de clase según el momento temporal y el segundo asociado al mensaje del chat (control del avance de la conversación)
    this.state = 0
    this.on(player, 'timeupdate', this.timeUpdateListener_)
  }

  createEl () {
    // Sin nombre (transcripción) la fila cambia de reparto: el texto sube a la línea del minutaje,
    // a su izquierda, en lugar de quedarse debajo. Lo resuelve el CSS con este modificador.
    const messageItem = dom.createEl('li', {
      className: this.options().message.userName ? 'upcoming' : 'upcoming chat-message--no-name'
    })
    return messageItem
  }

  /**
   * Evento que escucha cambios en la posición temporal del video
   * y actualiza la visualización del mensaje.
   *
   * @param  {Event} e
   */
  timeUpdateListener_ (e) {
    const time = this.player().currentTime()
    const start = this.options().message.relativeEventTime
    const end = this.options().endSeconds
    if (time < start && this.state !== 0) { // Mensaje futuro (state = 0)
      this.state = 0
      this.removeClass('current')
      this.addClass('upcoming')
    } else if (start <= time && (time < end || !end) && this.state !== 1) { // Mensaje actual (state = 1)
      this.state = 1
      this.addClass('current')
      this.removeClass('upcoming')
    } else if (end && time >= end && this.state !== 2) { // Mensaje pasado (state = 1)
      this.state = 2
      this.removeClass('current')
      this.removeClass('upcoming')
    }
  }
}
videojs.registerComponent('ProeducaChatEntryComponent', ProeducaChatEntryComponent)

/**
 * Botón para mostrar/ocultar el panel de contenidos de la grabación
 *
 * @extends Button
 * @class SeekButton
 */
class ProeducaChatButton extends Button {
  /**
   * Constructor
   *
   * @param {Player|Object} player The player
   * @param {Object=} options Button options
   */
  constructor (player, options) {
    super(player, options)
    this.controlText(this.localize('Session content'))
    player.addChild('ProeducaChatComponent', {
      hasChat: options.hasChat,
      hasTranscript: options.hasTranscript,
      messages: options.messages,
      summary: options.summary
    })
  }

  /**
   * Clase representativa del botón y su icono.
   *
   * @return {string} css cass string
   */
  buildCSSClass () {
    return 'vjs-proeduca-chat-button ' + super.buildCSSClass()
  }

  /**
   * Lanza el evento de apertura/cierre del panel. El evento sigue llamándose 'chat' aunque el
   * panel ya muestre más que el chat: renombrarlo no aporta nada y obligaría a tocar el toggle.
   */
  handleClick () {
    this.player().trigger('chat')
  }
}
videojs.registerComponent('ProeducaChatButton', ProeducaChatButton)

/**
 * Botón para el manejo de accesos directos desde el chat a posiciones del video
 *
 * @extends Button
 * @class SeekButton
 */
class ProeducaChatTimeButton extends Button {
  createEl () {
    const el = dom.createEl('button', {
      className: 'anchor'
    })
    el.innerHTML = this.options().text
    return el
  }

  /**
   * Mueve el video al segundo vinculado con el botón
   */
  handleClick () {
    this.player_.currentTime(this.options().seconds)
  }
}
videojs.registerComponent('ProeducaChatTimeButton', ProeducaChatTimeButton)

videojs.registerPlugin('proeducaChat', ProeducaChat)



// WEBPACK FOOTER //
// ./src/videojs/plugins/videojs-proeduca-chat.js